import { message } from 'antd'
import { useRolodexEdit } from 'features/fde'
import { useCallback } from 'react'
import { LockState, SignoffState } from 'types'

interface MetadataWithSignoff {
  lockState?: LockState
  signoff?: SignoffState
}

interface UseLockManagementProps {
  metadataEntryId: string
  metadataWithSignoff: MetadataWithSignoff
  updateFormData: (data: Partial<MetadataWithSignoff>) => void
}

interface UseLockManagementResult {
  isLocked: boolean
  startedSignoff: boolean
  lastLockTimestamp?: string
  handleStartSignoff: () => Promise<void>
  handleUnlock: () => Promise<void>
  canStartSignoff: boolean
  canSignAsReviewer: boolean
}

export const useLockManagement = ({
  metadataEntryId,
  metadataWithSignoff,
  updateFormData,
}: UseLockManagementProps): UseLockManagementResult => {
  const { rolodexPatch } = useRolodexEdit()

  const handleStartSignoff = useCallback(async (): Promise<void> => {
    if (!metadataEntryId) {
      message.error('No metadata entry found')
      return
    }

    try {
      const lockState = {
        isLocked: true,
        startedSignoff: true,
        lastLockTimestamp: new Date().toISOString(),
      }

      // Clear any existing signatures when locking
      const newData: Partial<MetadataWithSignoff> = {
        lockState,
        signoff: {
          lastRevision: metadataWithSignoff.signoff?.lastRevision || 0,
        },
      }

      // Update Rolodex entry
      await rolodexPatch([
        {
          id: metadataEntryId,
          data: {
            ...newData,
          },
        },
      ])

      // Update local state
      updateFormData(newData)
      message.success('Report locked for sign-off')
    } catch (error) {
      console.error('Failed to lock report:', error)
      message.error('Failed to lock report')
    }
  }, [
    metadataEntryId,
    metadataWithSignoff.signoff?.lastRevision,
    rolodexPatch,
    updateFormData,
  ])

  const handleUnlock = useCallback(async (): Promise<void> => {
    if (!metadataEntryId) {
      message.error('No metadata entry found')
      return
    }

    try {
      const newData: Partial<MetadataWithSignoff> = {
        lockState: {
          isLocked: false,
          startedSignoff: false,
          lastLockTimestamp: undefined,
        },
        signoff: {
          lastRevision: metadataWithSignoff.signoff?.lastRevision || 0,
        },
      }

      // Update Rolodex entry
      await rolodexPatch([
        {
          id: metadataEntryId,
          data: {
            ...newData,
          },
        },
      ])

      // Update local state
      updateFormData(newData)
      message.success('Report unlocked and signatures cleared')
    } catch (error) {
      console.error('Failed to unlock report:', error)
      message.error('Failed to unlock report')
    }
  }, [
    metadataEntryId,
    metadataWithSignoff.signoff?.lastRevision,
    rolodexPatch,
    updateFormData,
  ])

  // Computed properties
  const isLocked = metadataWithSignoff.lockState?.isLocked ?? false
  const startedSignoff = metadataWithSignoff.lockState?.startedSignoff ?? false
  const lastLockTimestamp = metadataWithSignoff.lockState?.lastLockTimestamp

  // Determine if signoff can be started
  const canStartSignoff =
    !isLocked && !metadataWithSignoff.signoff?.reviewerSignoff

  // Can only sign as reviewer if supervisor has signed and reviewer hasn't
  const canSignAsReviewer = Boolean(
    metadataWithSignoff.signoff?.supervisorSignoff &&
    !metadataWithSignoff.signoff?.reviewerSignoff
  )

  return {
    isLocked,
    startedSignoff,
    lastLockTimestamp,
    handleStartSignoff,
    handleUnlock,
    canStartSignoff,
    canSignAsReviewer,
  }
}
